<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="mx-auto max-w-5xl">
      <header class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <router-link
            to="/admin/noticias"
            class="mb-2 inline-flex items-center text-sm text-gray-400 transition-colors hover:text-white"
          >
            ← Noticias
          </router-link>
          <h1 class="text-2xl font-bold text-white sm:text-3xl">Categorías de noticias</h1>
          <p class="mt-1 text-sm text-gray-400">
            Organizá las noticias con categorías propias.
          </p>
        </div>
        <button type="button" class="btn-bochas-rojo text-sm" @click="openCreate">
          + Nueva categoría
        </button>
      </header>

      <div class="mb-4">
        <input
          v-model="search"
          type="search"
          placeholder="Buscar categoría"
          class="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-gray-500 focus:border-sky-400/50 sm:max-w-sm"
        />
      </div>

      <div v-if="loading" class="card-fbochas flex justify-center py-16">
        <span class="h-10 w-10 animate-spin rounded-full border-4 border-sky-400 border-t-transparent"></span>
      </div>

      <div
        v-else-if="error"
        class="mb-4 rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-200"
      >
        {{ error }}
      </div>

      <div v-else-if="filteredTags.length === 0" class="card-fbochas p-10 text-center">
        <p class="text-sm text-gray-400">
          {{ tags.length ? "No se encontraron categorías." : "Todavía no hay categorías." }}
        </p>
        <button
          v-if="!tags.length"
          type="button"
          class="btn-bochas-rojo mt-4 text-sm"
          @click="openCreate"
        >
          Crear la primera
        </button>
      </div>

      <div v-else class="grid gap-3 sm:grid-cols-2">
        <article
          v-for="tag in filteredTags"
          :key="tag.id"
          class="card-fbochas flex items-center gap-3 p-4"
        >
          <span
            class="h-11 w-2 shrink-0 rounded-full"
            :style="{ backgroundColor: tag.color || '#1D527C' }"
          ></span>
          <div class="min-w-0 flex-1">
            <h2 class="truncate font-semibold text-white">{{ tag.name }}</h2>
            <p class="truncate text-xs text-gray-500">{{ tag.slug }}</p>
          </div>
          <div class="flex shrink-0 gap-2">
            <button
              type="button"
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-sky-400/25 bg-sky-500/10 text-sky-200 transition-colors hover:bg-sky-500/20"
              title="Editar"
              aria-label="Editar categoría"
              @click="openEdit(tag)"
            >
              <IconFbochas name="edit" :size="16" />
            </button>
            <button
              type="button"
              class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-400/25 bg-red-500/10 text-red-300 transition-colors hover:bg-red-500/20"
              title="Eliminar"
              aria-label="Eliminar categoría"
              @click="onDelete(tag)"
            >
              <IconFbochas name="trash" :size="16" />
            </button>
          </div>
        </article>
      </div>

      <div
        v-if="modalOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        @click.self="closeModal"
      >
        <div class="card-fbochas w-full max-w-md border border-white/10 p-5 shadow-2xl sm:p-6">
          <div class="mb-5 flex items-center justify-between">
            <h2 class="text-lg font-bold text-white">
              {{ editingId ? "Editar categoría" : "Nueva categoría" }}
            </h2>
            <button
              type="button"
              class="text-gray-400 transition-colors hover:text-white"
              aria-label="Cerrar"
              @click="closeModal"
            >
              ✕
            </button>
          </div>

          <form class="space-y-4" @submit.prevent="onSave">
            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-300">Nombre</label>
              <input
                ref="nameInput"
                v-model="form.name"
                type="text"
                maxlength="100"
                required
                placeholder="Ej: Institucional"
                class="w-full rounded-xl border border-white/10 bg-bochas-bg px-4 py-3 text-sm text-white outline-none placeholder:text-gray-500 focus:border-sky-400/50"
              />
            </div>

            <div>
              <label class="mb-1.5 block text-sm font-medium text-gray-300">Color</label>
              <div class="flex items-center gap-3 rounded-xl border border-white/10 bg-bochas-bg px-3 py-2">
                <input
                  v-model="form.color"
                  type="color"
                  class="h-9 w-12 cursor-pointer rounded border-0 bg-transparent"
                />
                <input
                  v-model="form.color"
                  type="text"
                  maxlength="7"
                  pattern="#[0-9a-fA-F]{6}"
                  required
                  placeholder="#1D527C"
                  aria-label="Color hexadecimal"
                  class="min-w-0 flex-1 bg-transparent text-sm uppercase text-white outline-none placeholder:text-gray-500"
                />
              </div>
            </div>

            <p
              v-if="modalError"
              class="rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200"
            >
              {{ modalError }}
            </p>

            <div class="flex justify-end gap-2 pt-2">
              <button type="button" class="btn-bochas-trans text-sm" @click="closeModal">
                Cancelar
              </button>
              <button
                type="submit"
                class="btn-bochas-rojo text-sm"
                :disabled="saving"
                :class="{ 'cursor-not-allowed opacity-60': saving }"
              >
                {{ saving ? "Guardando..." : "Guardar" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { useConfirm } from "@/composables/useConfirm";
import {
  crearTag,
  editarTag,
  eliminarTag,
  listarTags,
} from "@/services/tags/tagsService";

const { confirm } = useConfirm();
const tags = ref([]);
const search = ref("");
const loading = ref(true);
const error = ref("");
const modalOpen = ref(false);
const modalError = ref("");
const saving = ref(false);
const editingId = ref(null);
const nameInput = ref(null);
const form = reactive({ name: "", color: "#1D527C" });

const filteredTags = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return tags.value;
  return tags.value.filter((tag) => tag.name?.toLowerCase().includes(query));
});

async function loadTags() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarTags();
    tags.value = Array.isArray(data) ? data : [];
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudieron cargar las categorías.";
  } finally {
    loading.value = false;
  }
}

function openCreate() {
  editingId.value = null;
  form.name = "";
  form.color = "#1D527C";
  modalError.value = "";
  modalOpen.value = true;
  nextTick(() => nameInput.value?.focus());
}

function openEdit(tag) {
  editingId.value = tag.id;
  form.name = tag.name || "";
  form.color = tag.color || "#1D527C";
  modalError.value = "";
  modalOpen.value = true;
  nextTick(() => nameInput.value?.focus());
}

function closeModal() {
  if (!saving.value) modalOpen.value = false;
}

async function onSave() {
  const name = form.name.trim();
  if (!name) return;

  saving.value = true;
  modalError.value = "";
  try {
    const payload = { name, color: form.color };
    if (editingId.value) await editarTag(editingId.value, payload);
    else await crearTag(payload);
    modalOpen.value = false;
    await loadTags();
  } catch (err) {
    modalError.value = err.response?.data?.message || "No se pudo guardar la categoría.";
  } finally {
    saving.value = false;
  }
}

async function onDelete(tag) {
  const confirmed = await confirm({
    title: "Eliminar categoría",
    message: `¿Eliminar “${tag.name}”? Las noticias asociadas quedarán sin categoría.`,
  });
  if (!confirmed) return;

  error.value = "";
  try {
    await eliminarTag(tag.id);
    await loadTags();
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudo eliminar la categoría.";
  }
}

onMounted(loadTags);
</script>
